import {
  Alert,
  Button,
  Grid,
  InputLabel,
  MenuItem,
  Select,
  TextField,
} from '@mui/material'
import { useLayout } from 'hooks/useLayout'
import { Modal } from 'components/Modal/Modal'
import { object, string } from 'yup'
import { yupResolver } from '@hookform/resolvers/yup'
import { ActivityButton } from 'components/ActivityButton'
import { isPresent } from '@jjvgaming/player-payback-library'
import { ModalFullBody } from 'components/Modal/ModalFullBody'
import { ModalFullFooter } from 'components/Modal/ModalFullFooter'
import { ModalFullHeader } from 'components/Modal/ModalFullHeader'
import { Controller, FormProvider, useForm } from 'react-hook-form'
import { type EditResourceDetailsProps } from './ResourcesDetailsSection'
import { FormFieldErrorMessage } from 'components/FormFieldErrorMessage/FormFieldErrorMessage'
import { type RequiredId } from 'utils/typeUtils'
import { type Resource } from 'types/api'
import { useGetResourceCategories } from 'hooks/api/Resources/useGetResourceCategories'

export const EditResourceDetails = ({
  resource,
  onSubmitResource,
  toggleModalIsOpen,
  updateIsActive,
  isModalOpen,
}: EditResourceDetailsProps) => {
  const { isMobile } = useLayout()
  const heightModal = isMobile ? '100%' : '60%'
  const getResourceCategoriesQuery = useGetResourceCategories()
  const categories = getResourceCategoriesQuery.data ?? []
  const orderedCategories = [...categories].sort((left, right) => {
    const leftIsNone = left.name.trim().toLowerCase() === 'none'
    const rightIsNone = right.name.trim().toLowerCase() === 'none'

    if (leftIsNone === rightIsNone) {
      return 0
    }

    return leftIsNone ? -1 : 1
  })

  const ResourceDetailsSchema = object().shape({
    Resource: object({
      title: string().required('Must enter a title'),
      description: string().nullable(),
      resourceCategoryId: string().required('Must select a category'),
      url: string().nullable(),
    }),
  })

  const formMethods = useForm({
    resolver: yupResolver(ResourceDetailsSchema),
    mode: 'all',
    defaultValues: {
      Resource: {
        title: resource.title,
        description: resource.description,
        resourceCategoryId:
          resource.resourceCategoryId == null
            ? ''
            : String(resource.resourceCategoryId),
        url: resource.url,
      },
    },
  })

  const {
    register,
    handleSubmit,
    formState: { errors },
  } = formMethods

  const handleSubmitWrapper = handleSubmit((data) => {
    const updatedResource: RequiredId<Resource> = {
      id: resource.id,
      title: data.Resource.title,
      description: data.Resource.description,
      resourceCategoryId: Number(data.Resource.resourceCategoryId),
      url: data.Resource.url,
      displayOrder: resource.displayOrder,
    }

    onSubmitResource(updatedResource)
  })

  return (
    <Modal
      isOpen={isModalOpen}
      toggleIsOpen={() => {}}
      sx={{
        width: isMobile ? '100%' : '40%',
      }}
    >
      <FormProvider {...formMethods}>
        <form onSubmit={handleSubmitWrapper} noValidate>
          <ModalFullHeader
            toggleIsOpen={toggleModalIsOpen}
            title={'Edit Resource'}
          />
          <ModalFullBody heightModal={heightModal}>
            <Grid container rowSpacing={2}>
              {errors.Resource && (
                <Grid item xs={12} pb={4}>
                  <Alert severity="error">
                    Please correct errors to continue
                  </Alert>
                </Grid>
              )}

              <Grid
                item
                container
                alignItems={'center'}
                xs={12}
                md={12}
                rowSpacing={3}
              >
                <Grid item alignItems={'center'} xs={12} md={6}>
                  <InputLabel required id="name" sx={{ fontWeight: 'normal' }}>
                    Title
                  </InputLabel>
                </Grid>
                <Grid item xs={12} md={6}>
                  <TextField
                    fullWidth
                    {...register('Resource.title', {
                      required: true,
                    })}
                    error={isPresent(errors.Resource?.title)}
                    sx={{ width: '100%' }}
                  />
                  {errors.Resource?.title?.message && (
                    <FormFieldErrorMessage
                      message={errors.Resource.title.message}
                    />
                  )}
                </Grid>

                <Grid item alignItems={'center'} xs={12} md={6}>
                  <InputLabel id="description" sx={{ fontWeight: 'normal' }}>
                    Description
                  </InputLabel>
                </Grid>
                <Grid item alignItems={'center'} xs={12} md={6}>
                  <TextField
                    fullWidth
                    {...register('Resource.description', {
                      required: true,
                    })}
                    error={isPresent(errors.Resource?.description)}
                    sx={{ width: '100%' }}
                  />
                </Grid>

                <Grid item alignItems={'center'} xs={12} md={6}>
                  <InputLabel
                    id="category"
                    sx={{ fontWeight: 'normal' }}
                    required
                  >
                    Category
                  </InputLabel>
                </Grid>
                <Grid item alignItems={'center'} xs={12} md={6}>
                  <Controller
                    name="Resource.resourceCategoryId"
                    render={({ field }) => (
                      <Select
                        {...field}
                        value={field.value ?? ''}
                        displayEmpty
                        renderValue={(value) => {
                          if (value == null || value === '') {
                            return 'Select Category'
                          }

                          const selectedCategory = orderedCategories.find(
                            (category) => String(category.id) === String(value)
                          )

                          return selectedCategory?.name ?? String(value)
                        }}
                        labelId="Resource.resourceCategoryId"
                        variant="outlined"
                        fullWidth
                        id="Resource.resourceCategoryId"
                        error={isPresent(errors.Resource?.resourceCategoryId)}
                      >
                        <MenuItem value="" disabled>
                          Select Category
                        </MenuItem>
                        {orderedCategories.map((category) => (
                          <MenuItem
                            value={String(category.id)}
                            key={category.id}
                          >
                            {category.name}
                          </MenuItem>
                        ))}
                      </Select>
                    )}
                  />
                  {errors.Resource?.resourceCategoryId?.message && (
                    <FormFieldErrorMessage
                      message={errors.Resource?.resourceCategoryId?.message}
                    />
                  )}
                </Grid>
                {!resource.fileName ? (
                  <>
                    <Grid item alignItems={'center'} xs={12} md={6}>
                      <InputLabel id="URL" sx={{ fontWeight: 'normal' }}>
                        URL
                      </InputLabel>
                    </Grid>
                    <Grid item alignItems={'center'} xs={12} md={6}>
                      <TextField
                        fullWidth
                        {...register('Resource.url', {
                          required: true,
                        })}
                        error={isPresent(errors.Resource?.url)}
                        sx={{ width: '100%' }}
                      />
                    </Grid>
                  </>
                ) : null}
              </Grid>
            </Grid>
          </ModalFullBody>
          <ModalFullFooter>
            <Button variant="text" onClick={toggleModalIsOpen}>
              Cancel
            </Button>
            <ActivityButton
              active={updateIsActive}
              type="submit"
              variant="contained"
            >
              Save
            </ActivityButton>
          </ModalFullFooter>
        </form>
      </FormProvider>
    </Modal>
  )
}
